import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { DownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Radio,
  Stack,
  Button,
  Dialog,
  Divider,
  TextField,
  FormLabel,
  RadioGroup,
  Typography,
  DialogTitle,
  FormControl,
  Autocomplete,
  DialogActions,
  DialogContent,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { List, ListEntityType } from 'src/services/lists/lists.types';
import { useGetListsPaginatedQuery } from 'src/services/lists/lists.service';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { saveFile, downloadDataExport } from 'src/services/data-export/data-export.service';
import { useGetConfigByEntityTypeQuery } from 'src/services/custom-fields/custom-field.service';
import { DataExportError, DataExportProgress } from 'src/services/data-export/data-export.types';

import { ImportEntity } from 'src/sections/import/import-fields';
import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { BusinessErrorCodes } from 'src/types/business-errors';

import ExportFieldPicker from './export-field-picker';
import { isDefaultSelected, buildExportTargets, missingForReimport } from './export-fields';

const ENTITIES: ImportEntity[] = ['contact', 'company', 'candidate'];

const CUSTOM_FIELD_ENTITY: Record<ImportEntity, CustomFieldEntityType> = {
  contact: CustomFieldEntityType.CONTACT,
  company: CustomFieldEntityType.COMPANY,
  candidate: CustomFieldEntityType.CANDIDATE,
};

const LIST_ENTITY: Record<ImportEntity, ListEntityType> = {
  contact: ListEntityType.CONTACT,
  company: ListEntityType.COMPANY,
  candidate: ListEntityType.CANDIDATE,
};

type Scope = 'all' | 'list';

export type DataExportDialogProps = {
  open: boolean;
  onClose: VoidFunction;
  /** The organisation whose records are exported: the signed-in one, or a customer's for a super admin. */
  organizationId: string;
  /** Opened from a list's page: the record type and the list are fixed. */
  list?: Pick<List, 'id' | 'name' | 'entity_type' | 'entity_count'>;
};

/**
 * Exports contacts, companies or candidates, or one list's members, as a CSV with the columns the
 * admin ticks (JT1-1913). The columns are the importer's, so the file imports back unchanged.
 */
export default function DataExportDialog({ open, onClose, organizationId, list }: DataExportDialogProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { canAccess } = useFeatures();
  const authContext = useAuthContext();
  const tenantType = useOrgTenant();

  // A super admin exporting for a customer: the signed-in plan says nothing about theirs, so the
  // server decides, as the importer does.
  const onBehalfOfOrganization = Boolean(authContext?.organization?.id) && authContext.organization.id !== organizationId;
  const listsEnabled = onBehalfOfOrganization || canAccess(FeatureType.LISTS_ENABLED);
  const customFieldsEnabled = onBehalfOfOrganization || canAccess(FeatureType.CUSTOM_FIELDS_ENABLED);

  const [chosenEntity, setChosenEntity] = useState<ImportEntity>('contact');
  const [scope, setScope] = useState<Scope>('all');
  const [pickedList, setPickedList] = useState<List | null>(null);
  const [listSearch, setListSearch] = useState('');
  /** Columns the user ticked or unticked; every other column keeps its default. */
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  const [progress, setProgress] = useState<DataExportProgress | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entity: ImportEntity = (list?.entity_type as ImportEntity | undefined) ?? chosenEntity;

  const { data: customFields } = useGetConfigByEntityTypeQuery(
    { entityType: CUSTOM_FIELD_ENTITY[entity], organizationId: onBehalfOfOrganization ? organizationId : undefined },
    { skip: !open || !customFieldsEnabled }
  );

  const onListSearch = useMemo(() => debounce((text: string) => setListSearch(text), 400), []);
  useEffect(() => () => onListSearch.cancel(), [onListSearch]);

  const { currentData: lists, isFetching: listsLoading } = useGetListsPaginatedQuery(
    {
      page: 1,
      per_page: 20,
      entity_type: LIST_ENTITY[entity],
      search_query: listSearch,
      organization_id: onBehalfOfOrganization ? organizationId : undefined,
    },
    { skip: !open || !!list || !listsEnabled || scope !== 'list' }
  );

  const targets = useMemo(
    () => buildExportTargets(entity, customFieldsEnabled ? customFields : undefined, t),
    [entity, customFields, customFieldsEnabled, t]
  );

  const selected = useMemo(
    () => new Set(targets.filter((target) => overrides.get(target.id) ?? isDefaultSelected(target)).map((target) => target.id)),
    [targets, overrides]
  );

  const missing = useMemo(() => missingForReimport(entity, selected, targets), [entity, selected, targets]);

  const exportListId = list?.id ?? (scope === 'list' ? pickedList?.id : undefined);
  const ready = selected.size > 0 && (scope === 'all' || !!list || !!pickedList);

  const reset = useCallback(() => {
    setChosenEntity('contact');
    setScope('all');
    setPickedList(null);
    setOverrides(new Map());
    setProgress(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (exporting) return;
    reset();
    onClose();
  }, [exporting, onClose, reset]);

  const handleEntityChange = (next: ImportEntity) => {
    setChosenEntity(next);
    setPickedList(null);
    setOverrides(new Map());
  };

  const handleFieldsChange = useCallback((ids: string[], checked: boolean) => {
    setOverrides((current) => {
      const next = new Map(current);
      ids.forEach((id) => next.set(id, checked));
      return next;
    });
  }, []);

  const errorMessage = useCallback(
    (err: unknown): string => {
      if (err instanceof DataExportError) {
        if (err.status === 401 || err.status === 403) return t('exceptions.unauthorized');
        if (err.errorCode === BusinessErrorCodes.DataExportTooLarge) {
          return t('data_export.errors.too_large', {
            total: Number(err.context?.total ?? 0).toLocaleString(),
            max: Number(err.context?.max ?? 0).toLocaleString(),
          });
        }
        if (err.errorCode === BusinessErrorCodes.DataExportUnknownField) return t('data_export.errors.unknown_field');
      }
      return t('data_export.errors.failed');
    },
    [t]
  );

  const handleExport = useCallback(async () => {
    setError(null);
    setExporting(true);
    setProgress(null);

    try {
      const result = await downloadDataExport(
        organizationId,
        {
          entity,
          list_id: exportListId,
          fields: targets.filter((target) => selected.has(target.id)).map((target) => ({ key: target.id, header: target.label })),
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        setProgress
      );

      saveFile(result.blob, result.fileName);
      enqueueSnackbar(t('data_export.success', { records: result.total.toLocaleString() }), { variant: 'success' });
      setExporting(false);
      reset();
      onClose();
    } catch (err) {
      setExporting(false);
      setError(errorMessage(err));
    }
  }, [entity, enqueueSnackbar, errorMessage, exportListId, onClose, organizationId, reset, selected, t, targets]);

  const renderScope = () => {
    if (list) {
      return (
        <Alert severity="info">
          {t('data_export.list_scope', { name: list.name, records: (list.entity_count ?? 0).toLocaleString() })}
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        <FormControl>
          <FormLabel>{t('data_export.entity_label')}</FormLabel>
          <RadioGroup row value={entity} onChange={(event) => handleEntityChange(event.target.value as ImportEntity)}>
            {ENTITIES.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio size="small" />}
                label={t(`data_export.entities.${option}`)}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {listsEnabled && (
          <FormControl>
            <FormLabel>{t('data_export.scope_label')}</FormLabel>
            <RadioGroup row value={scope} onChange={(event) => setScope(event.target.value as Scope)}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={t('data_export.scope_all')} />
              <FormControlLabel value="list" control={<Radio size="small" />} label={t('data_export.scope_list')} />
            </RadioGroup>

            {scope === 'list' && (
              <Autocomplete
                sx={{ mt: 1, maxWidth: 420 }}
                size="small"
                options={lists?.results ?? []}
                loading={listsLoading}
                value={pickedList}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // The server already filtered by the search text.
                filterOptions={(all) => all}
                onChange={(_, value) => setPickedList(value)}
                onInputChange={(_, text, reason) => {
                  if (reason === 'input') onListSearch(text);
                }}
                renderInput={(params) => <TextField {...params} label={t('data_export.list_pick')} />}
              />
            )}
          </FormControl>
        )}
      </Stack>
    );
  };

  const renderProgress = () => {
    const total = progress?.total ?? 0;
    const written = progress?.written ?? 0;

    return (
      <Stack spacing={1}>
        <Typography variant="body2">
          {total > 0
            ? t('data_export.progress', { written: written.toLocaleString(), total: total.toLocaleString() })
            : t('data_export.preparing')}
        </Typography>
        <LinearProgress
          aria-label={t('data_export.preparing')}
          variant={total > 0 ? 'determinate' : 'indeterminate'}
          value={total > 0 ? Math.round((written / total) * 100) : 0}
        />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DownloadOutlined />
          <Typography variant="inherit">{t(list ? 'data_export.title_list' : 'data_export.title')}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {renderScope()}

          <Divider />

          <ExportFieldPicker targets={targets} selected={selected} onChange={handleFieldsChange} />

          {entity === 'candidate' && (onBehalfOfOrganization || tenantType === TenantType.Client) && (
            <Alert severity="info">{t('data_export.privileged_note')}</Alert>
          )}

          {selected.size > 0 && missing.length > 0 && (
            <Alert severity="warning">{t('data_export.reimport_warning', { fields: missing.join(', ') })}</Alert>
          )}

          {exporting && renderProgress()}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          startIcon={<DownloadOutlined />}
          loading={exporting}
          disabled={!ready}
          onClick={handleExport}
        >
          {t('data_export.export_button')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
